import { useState, useEffect, useCallback } from 'react';

const KEY = 'app_theme';

// '' means follow the phone's own light/dark setting -- not the box's, which is
// a separate, server-side setting on the Screen sheet. The stylesheet is
// written for exactly these three
// states: tokens on bare :root, redefined under prefers-color-scheme for the
// un-stamped case, and redefined again under [data-theme="dark"] so an explicit
// choice wins in both directions.
const VALID = ['', 'light', 'dark'];

const read = () => {
  try {
    const saved = localStorage.getItem(KEY);
    return VALID.includes(saved) ? saved : '';
  } catch {
    // A private window cannot remember the choice. Following the phone is a
    // reasonable answer rather than a failure.
    return '';
  }
};

const apply = (theme) => {
  const root = document.documentElement;
  if (theme) {
    root.setAttribute('data-theme', theme);
  } else {
    root.removeAttribute('data-theme');
  }

  // The browser chrome and the splash behind an installed app follow this, so
  // leaving it on the light teal while the page went dark looked like a seam.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    const dark = theme === 'dark'
      || (!theme && window.matchMedia?.('(prefers-color-scheme: dark)').matches);
    meta.setAttribute('content', dark ? '#14100a' : '#0e4e5c');
  }
};

/**
 * Light, dark, or whatever the phone says.
 *
 * Someone checks this at Fajr, in the dark, and the phone is already in its
 * night mode -- so following the phone is the default and the two explicit
 * choices are for people whose preference differs from their phone's.
 */
export const useTheme = () => {
  const [theme, setTheme] = useState(read);

  useEffect(() => { apply(theme); }, [theme]);

  // While following the phone, the page has to change when the phone does.
  useEffect(() => {
    if (theme) return undefined;
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!media) return undefined;

    const onChange = () => apply('');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [theme]);

  const choose = useCallback((next) => {
    const value = VALID.includes(next) ? next : '';
    setTheme(value);
    try {
      if (value) localStorage.setItem(KEY, value);
      else localStorage.removeItem(KEY);
    } catch { /* the choice still holds for this visit */ }
  }, []);

  return { theme, choose };
};
